import { useEffect } from 'react'
import { Typography, TextField } from '@material-ui/core'
import Grid from '@material-ui/core/Grid'
import { useFormikContext } from 'formik'
import DateInput from '../../../components/DateInput'
import NumericInput from '../../../components/Formik/Forms/NumericInput'
import TextInput from '../../../components/Formik/Forms/TextInput'
import { Perna } from '../types'
import { isTipoContrato } from '../utils'
import { formatLabelPerson } from 'src/modules/pessoas/utils'
import { PagadorTomadorFields } from './dados-complementares/PagadorTomadorFields'
import { RemetenteFields } from './dados-complementares/RemetenteFields'
import { DestinatarioFields } from './dados-complementares/DestinatarioFields'
import { LocalColetaFields } from './dados-complementares/LocalColetaFields'
import { LocalEntregaFields } from './dados-complementares/LocalEntregaFields'
import { apiSauceInstance } from 'src/services/api'

interface Props {
  isRestrictedEdit: boolean
  centroCustoId: string
  grupoNegociadorId: string
}

export const InformacoesAdicionais = ({
  isRestrictedEdit,
  centroCustoId,
  grupoNegociadorId
}: Props) => {
  // Tipamos o filialEmissao no Formik esperando o formato { name: string; value: string }
  const { values, setFieldValue } = useFormikContext<{
    tipoContratacao: string
    Pernas: Perna[]
    filialEmissao?: { name: string; value: string } | null
  }>()

  useEffect(() => {
    let isMounted = true

    const buscarFilialComoNoPureAsync = async () => {
      if (!centroCustoId) {
        if (isMounted) setFieldValue('filialEmissao', null)
        return
      }

      try {
        // 1. Faz a chamada HTTP com os exatos parâmetros que o PureAsync enviaria
        const response = await apiSauceInstance.get<any>('/pessoas', {
          limit: 10,
          ativo: true,
          centroCustoId
        })

        if (response.ok && isMounted) {
          // 2. Extrai os nós do 'edges' exatamente igual ao PureAsync
          const edges = response.data?.edges || []

          if (edges.length > 0) {
            // 3. Pega o primeiro registro e formata usando a sua função utilitária
            const primeiraFilial = edges[0]
            const filialFormatada = formatLabelPerson(primeiraFilial) // Retorna { name, value }

            setFieldValue('filialEmissao', filialFormatada)
          } else {
            setFieldValue('filialEmissao', null)
          }
        }
      } catch (error) {
        console.error('Erro ao buscar filial automaticamente:', error)
        if (isMounted) setFieldValue('filialEmissao', null)
      }
    }

    buscarFilialComoNoPureAsync()

    return () => {
      isMounted = false
    }
  }, [centroCustoId, setFieldValue])

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h5">Informações Adicionais</Typography>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextInput
          name="numeroPedido"
          label="Número do Pedido (opcional)"
          disabled={isRestrictedEdit}
        />
      </Grid>

      <Grid item xs={12} sm={3}>
        <DateInput
          label="Data Início Retirada"
          name="dataInicio"
          disabled={isRestrictedEdit}
        />
      </Grid>

      <Grid item xs={12} sm={3}>
        <DateInput label="Data Limite Retirada" name="dataTermino" />
      </Grid>

      {!isTipoContrato(values.tipoContratacao) && (
        <Grid item xs={12} sm={12}>
          <TextInput
            name="cadencia"
            label="Cadência para Retirada (opcional)"
            disabled={isRestrictedEdit}
          />
        </Grid>
      )}

      <Grid item xs={12} sm={12}>
        <TextInput
          name="observacao"
          label="Observação (opcional)"
          disabled={isRestrictedEdit}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <NumericInput
          label="Dias para carregar"
          name="diasParaCarregar"
          precision={0}
          disabled={isRestrictedEdit}
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <NumericInput
          label="Dias para descarregar"
          name="diasParaDescarregar"
          precision={0}
          disabled={isRestrictedEdit}
        />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6">Dados Complementares</Typography>
      </Grid>

      {/* Input trancado e limpo */}
      <Grid item xs={12} sm={3}>
        <TextField
          label="Filial de Emissão"
          // Como o dado salvo já está formatado com { name, value }, exibimos apenas o .name
          value={values.filialEmissao?.name || ''}
          disabled
          fullWidth
          variant="outlined"
          placeholder={
            !centroCustoId ? 'Selecione o centro de custo...' : 'Buscando...'
          }
          InputLabelProps={{
            shrink: true
          }}
        />
      </Grid>

      <PagadorTomadorFields
        isRestrictedEdit={isRestrictedEdit}
        grupoNegociadorId={grupoNegociadorId}
      />
      <RemetenteFields isRestrictedEdit={isRestrictedEdit} />
      <DestinatarioFields isRestrictedEdit={isRestrictedEdit} />
      <LocalColetaFields isRestrictedEdit={isRestrictedEdit} />
      <LocalEntregaFields isRestrictedEdit={isRestrictedEdit} />
    </Grid>
  )
}
